import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Heart, LogOut, User, MessageSquare, Mail, Shield, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useUserTypes } from "@/hooks/useUserTypes";
import { useUnreadConversations } from "@/hooks/useUnreadConversations";
import { useExposeNavHeight } from "@/hooks/useExposeNavHeight";
import { useTenant } from "@/hooks/useTenant";
import { useSiteAsset } from "@/hooks/useSiteAsset";

const Navbar = () => {
  const tenant = useTenant();
  // Admin-uploaded wordmark (Site assets → SEO & brand → Logo); none by default.
  const logo = useSiteAsset("brand.logo");
  const hasLogo = !logo.isFallback && !!logo.src;
  const navRef = useRef<HTMLElement>(null);
  useExposeNavHeight(navRef);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { data: isAdmin = false } = useIsAdmin();
  const { isOwner } = useUserTypes();
  const messagesBadge = useUnreadConversations();

  // Admins see the same consumer nav as everyone else, they get a Shield
  // link in the icon row (and a Mobile menu entry) to jump back into /admin.
  // (Previously isAdmin replaced the entire nav with just "Admin Dashboard",
  // which made it impossible for admins to browse the consumer site.)
  // Owners/managers get both "Show homes" (browse the marketplace)
  // and an extra "My Listed Homes" tab; renters just get the former.
  const desktopLinks = [
    { to: "/", label: "Home" },
    // "Show homes", not "Show Available Homes": renters read "available" as
    // free on their dates (Angelina Oct 23, Felipe approved Oct 25).
    { to: "/properties", label: "Show homes" },
    ...(isOwner ? [{ to: "/dashboard", label: "My Homes" }, { to: "/pricing", label: "Promote" }] : []),
    { to: "/areas", label: "Explore Areas" },
    { to: "/about", label: "About & Contact" },
  ];

  return (
    <>
      <nav ref={navRef} className="sticky top-0 left-0 right-0 z-nav glass-surface border-b border-border/40">
        <div className="container mx-auto px-4 py-1.5 flex items-center justify-between relative gap-3">
          {/* Home link. Shows the city's logo when one is uploaded; otherwise it
              stays a plain click target so the layout is unchanged. */}
          <Link
            to="/"
            aria-label="Home"
            className={hasLogo ? "inline-flex items-center h-11 -ml-1 pr-2 min-w-0" : "inline-flex items-center justify-center h-11 w-11 -ml-2"}
          >
            {hasLogo && (
              <img src={logo.src} alt={tenant.siteName} className="h-8 w-auto max-w-[160px] object-contain" decoding="async" />
            )}
          </Link>
          <div className="flex items-center gap-1">
            {/* ⌘K command palette trigger, visible only on desktop where the
                shortcut is also bound. The button dispatches a window event
                that AppLayout listens for. */}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("msth:open-command-palette"))}
              className="hidden md:inline-flex items-center gap-2 h-8 px-3 rounded-full border border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/60 text-[12px] transition-colors"
              aria-label="Open command palette"
            >
              <Search className="h-3 w-3" />
              <span>Search</span>
              <kbd className="ml-1 text-[10px] font-mono text-muted-foreground/80 bg-muted px-1 py-0.5 rounded">⌘K</kbd>
            </button>
            {user && (
              <>
                {!isOwner && (
                  <Link to="/favorites" className="hidden md:inline-flex">
                    <Button variant="ghost" size="icon" className="rounded-full h-8 w-8"><Heart className="h-3.5 w-3.5" /></Button>
                  </Link>
                )}
                <Link to="/messages" className="hidden md:inline-flex">
                  <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 relative" title="Messages">
                    <MessageSquare className="h-3.5 w-3.5" />
                    {messagesBadge > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center leading-none">
                        {messagesBadge > 9 ? "9+" : messagesBadge}
                      </span>
                    )}
                  </Button>
                </Link>
                <Link
                  to="/profile"
                  state={!isOwner ? { openPreferences: true } : undefined}
                  className="hidden md:inline-flex"
                >
                  <Button variant="ghost" size="icon" className="rounded-full h-8 w-8"><User className="h-3.5 w-3.5" /></Button>
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="hidden md:inline-flex" title="Admin dashboard">
                    <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-primary">
                      <Shield className="h-3.5 w-3.5" />
                    </Button>
                  </Link>
                )}
              </>
            )}
            {user && (
              <Button variant="ghost" size="sm" className="hidden md:inline-flex gap-1.5 text-[13px]" onClick={signOut}>
                <LogOut className="h-3 w-3" /> Sign Out
              </Button>
            )}
            {!user && (
              <Link to="/login" className="md:hidden">
                <Button size="sm" className="rounded-full h-9 px-3.5 text-[13px]">Sign In</Button>
              </Link>
            )}
            <button
              className="md:hidden h-11 w-11 -mr-2 inline-flex items-center justify-center"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              aria-expanded={open}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div className="hidden md:flex items-center justify-center gap-8 py-3">
          {desktopLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`text-[12px] tracking-[0.12em] font-medium uppercase transition-colors ${
                location.pathname === l.to
                  ? "text-primary"
                  : "text-foreground hover:text-primary"
              }`}
            >
              {l.label}
            </Link>
          ))}
          {!user && (
            <Link
              to="/login"
              className="text-[12px] tracking-[0.12em] font-semibold uppercase text-primary hover:text-primary-hover transition-colors"
            >
              Sign In / Sign Up
            </Link>
          )}
        </div>
      </nav>

      {open && (
        <div className="md:hidden fixed inset-x-0 bottom-0 top-nav glass-surface z-menu px-6 pb-24 pt-4 animate-fade-in overflow-y-auto safe-area-top">
          <div className="space-y-1">
            {isAdmin && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 py-4 text-[17px] font-medium transition-colors border-b border-border/50 ${location.pathname.startsWith("/admin") ? "text-primary" : "text-foreground"}`}
              >
                <Shield className="h-4 w-4 text-primary" /> Admin Dashboard
              </Link>
            )}
            <Link to="/" onClick={() => setOpen(false)}
              className={`flex items-center gap-3 py-4 text-[17px] font-medium transition-colors border-b border-border/50 ${location.pathname === "/" ? "text-primary" : "text-foreground"}`}>
              Home
            </Link>
            {user && (
              <Link to="/profile" state={!isOwner ? { openPreferences: true } : undefined} onClick={() => setOpen(false)}
                className={`flex items-center gap-3 py-4 text-[17px] font-medium transition-colors border-b border-border/50 ${location.pathname === "/profile" ? "text-primary" : "text-foreground"}`}>
                My Profile
              </Link>
            )}
            <Link to="/properties" onClick={() => setOpen(false)}
              className={`flex items-center gap-3 py-4 text-[17px] font-medium transition-colors border-b border-border/50 ${location.pathname === "/properties" ? "text-primary" : "text-foreground"}`}>
              Show homes
            </Link>
            {isOwner && (
              <Link to="/dashboard" onClick={() => setOpen(false)}
                className={`flex items-center gap-3 py-4 text-[17px] font-medium transition-colors border-b border-border/50 ${location.pathname === "/dashboard" ? "text-primary" : "text-foreground"}`}>
                My Homes
              </Link>
            )}
            {user && !isOwner && (
              <Link to="/favorites" onClick={() => setOpen(false)}
                className={`flex items-center gap-3 py-4 pl-4 text-[15px] transition-colors border-b border-border/50 ${location.pathname === "/favorites" ? "text-primary" : "text-muted-foreground"}`}>
                Saved Properties
              </Link>
            )}
            <Link to="/areas" onClick={() => setOpen(false)}
              className={`flex items-center gap-3 py-4 text-[17px] font-medium transition-colors border-b border-border/50 ${location.pathname === "/areas" ? "text-primary" : "text-foreground"}`}>
              Explore Areas
            </Link>
            <Link to="/about" onClick={() => setOpen(false)}
              className={`flex items-center gap-3 py-4 text-[17px] font-medium transition-colors border-b border-border/50 ${location.pathname === "/about" ? "text-primary" : "text-foreground"}`}>
              About & Contact
            </Link>
          </div>
          <div className="mt-8">
            {user ? (
              <button onClick={() => { signOut(); setOpen(false); }} className="w-full py-4 text-[17px] font-semibold text-destructive text-center rounded-xl border border-destructive/20 bg-destructive/5">
                Sign Out
              </button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="flex items-center justify-center gap-2 w-full py-4 text-[17px] font-semibold text-center rounded-full bg-primary text-primary-foreground hover:bg-primary-hover transition-colors">
                <Mail className="h-4 w-4" />
                Sign Up
              </Link>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
